import { useContext, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Label from '../../../components/form/Label'
import catchError from '../../../components/global/CatchError'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import { StyledButtonLink } from '../../../components/table/Td'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { AdminContainer, TitleWrapper } from '../users/AdminUsers'
import { AdminForm } from '../users/AdminUsersForm'

const AdminBadnetQueueForm = () => {
    const user = useContext(UserContext)
    const location = useLocation()
    const params = useParams()

    const { t } = useTranslation('translation')

    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()
    const [currentLang, setCurrentLang] = useState(lng)

    const navigate = useNavigate()
    const BACK_LINK = `/${lng}/bva-admin/badnet-queue`

    const [action, setAction] = useState('sync_tournaments')
    const [payload, setPayload] = useState('')
    const [priority, setPriority] = useState(0)
    const [scheduledAt, setScheduledAt] = useState('')

    const [isLoading, setIsLoading] = useState(true)

    const isAddForm = location.pathname.includes('/bva-admin/badnet-queue/creer')

    const getPayloadPlaceholder = (action: string) => {
        switch (action) {
            case 'sync_tournaments':
                return '{\n  "ligueId": 3306,\n  "saveTranslations": false\n}'
            case 'sync_tournament':
                return '{\n  "badnetId": 12345\n}'
            case 'update_member_count':
                return '{\n  "badnetId": 12345\n}'
            default:
                return '{}'
        }
    }

    const handleActionChange = (newAction: string) => {
        setAction(newAction)
        if (isAddForm) {
            setPayload(getPayloadPlaceholder(newAction))
        }
    }

    useEffect(() => {
        verifyToken()
        if (!isAddForm && isLoading) {
            setCurrentLang(lng)
            apiClient
                .get(`/badnet/queue/jobs/${params.id}`)
                .then((res) => {
                    setAction(res.data.data.action)
                    setPayload(JSON.stringify(res.data.data.payload, null, 2))
                    setPriority(res.data.data.priority || 0)
                    setScheduledAt(
                        res.data.data.scheduledAt ? new Date(res.data.data.scheduledAt).toISOString().slice(0, 16) : '',
                    )
                    setIsLoading(false)
                })
                .catch((err) => {
                    catchError(err.response.data)
                })
        } else {
            if (isAddForm && !payload) {
                setPayload(getPayloadPlaceholder(action))
            }
            setIsLoading(false)
        }
    }, [isAddForm, isLoading, params.id, lng, action, payload])

    if (isLoading || currentLang !== lng) return <Loader />

    const isPayloadValidate = () => {
        try {
            if (payload) {
                JSON.parse(payload)
            }
            return true
        } catch {
            return false
        }
    }

    const isValidate = action && isPayloadValidate()

    const submit = () => {
        verifyToken()
        try {
            const parsedPayload = payload ? JSON.parse(payload) : {}

            if (isAddForm) {
                const data: any = {
                    action,
                    payload: parsedPayload,
                }
                if (priority > 0) data.priority = priority
                if (scheduledAt) data.scheduledAt = new Date(scheduledAt).toISOString()

                apiClient
                    .post('/badnet/queue/create-job', data)
                    .then(() =>
                        sweetAlertSuccess(`Le job a bien été créé.`).then(() => {
                            navigate(BACK_LINK)
                        }),
                    )
                    .catch((err) => {
                        catchError(err.response.data)
                    })
            } else {
                const data: any = {
                    action,
                    payload: parsedPayload,
                }
                if (priority > 0) data.priority = priority
                if (scheduledAt) data.scheduledAt = new Date(scheduledAt).toISOString()

                apiClient
                    .patch(`/badnet/queue/jobs/${params.id}`, data)
                    .then(() =>
                        sweetAlertSuccess(`Le job a bien été modifié.`).then(() => {
                            navigate(BACK_LINK)
                        }),
                    )
                    .catch((err) => {
                        catchError(err.response.data)
                    })
            }
        } catch (error) {
            catchError({ message: 'Invalid JSON payload' })
        }
    }

    return (
        <>
            <TitleWrapper>
                <Title>{isAddForm ? 'Créer' : 'Modifier'} un job</Title>
                <StyledButtonLink to={BACK_LINK} className="btn btn-primary mb-2">
                    Retour
                </StyledButtonLink>
            </TitleWrapper>
            <AdminContainer>
                <AdminForm>
                    <Form.Group className="mb-3">
                        <Label>Action *</Label>
                        <Form.Select onChange={(e) => handleActionChange(e.target.value)} value={action}>
                            <option value="sync_tournaments">Sync Tournaments</option>
                            <option value="sync_tournament">Sync Tournament</option>
                            <option value="update_member_count">Update Member Count</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Payload (JSON)</Label>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            placeholder={getPayloadPlaceholder(action)}
                            onChange={(e) => setPayload(e.target.value)}
                            value={payload}
                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                        />
                        {!isPayloadValidate() && <small style={{ color: 'red' }}>Invalid JSON format</small>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Priority</Label>
                        <Form.Control
                            type="number"
                            placeholder="0"
                            onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                            value={priority}
                            min="0"
                        />
                        <small style={{ color: '#666' }}>Higher priority jobs are processed first</small>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Scheduled At (optional)</Label>
                        <Form.Control
                            type="datetime-local"
                            onChange={(e) => setScheduledAt(e.target.value)}
                            value={scheduledAt}
                        />
                        <small style={{ color: '#666' }}>Leave empty for immediate execution</small>
                    </Form.Group>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={submit}
                        disabled={!isValidate}
                        style={{ marginTop: '1rem' }}
                    >
                        {isAddForm ? 'Créer' : 'Modifier'}
                    </button>
                </AdminForm>
            </AdminContainer>
        </>
    )
}

export default AdminBadnetQueueForm
